import React from "react";
import { Box, Grid, IconButton, Typography, Paper } from "@mui/material";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useTranslation } from "react-i18next";
import InfoButton from "./InfoButton";
import { ComoInput } from "../Common/ComoInput";
import { ComoButton } from "../Common/ComoButton";
import colors from "../../Constants/colors";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";

interface KnowledgeBaseFormState {
  knowledgebase: string[];
  trends: string[];
}

interface KnowledgeBaseAndCurrentTrendsProps {
  formState: KnowledgeBaseFormState;
  handleLinkChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    section: "knowledgebase" | "trends",
    index: number
  ) => void;
  handleAddLink: (section: "knowledgebase" | "trends") => void;
  handleRemoveLink: (
    section: "knowledgebase" | "trends",
    index: number
  ) => void;
}

const KnowledgeBaseAndCurrentTrends: React.FC<
  KnowledgeBaseAndCurrentTrendsProps
> = ({ formState, handleLinkChange, handleAddLink, handleRemoveLink }) => {
  const { t } = useTranslation("expertForm");
  const { colors: workspace } = useWorkspaceColors();
  return (
    <Grid item xs={12}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          border: `1px solid ${colors.gray200}`,
          borderLeft: `4px solid ${workspace.primary_dark}`,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: workspace.primary_dark,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            mb: 3,
          }}
        >
          {t("expertForm.knowledgeBaseAndCurrentTrends")}
          <InfoButton
            info={t("expertForm.knowledgeBaseAndCurrentTrendsInfo")}
          />
        </Typography>
        <Grid container spacing={3} justifyContent="center">
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              paddingLeft: "0 !important",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                width: "100%",
                marginBottom: "10px",
                pl: 3,
                pr: 3,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  color: workspace.primary_dark,
                  fontWeight: 600,
                  textAlign: "left",
                }}
              >
                {t("expertForm.knowledgebaseLinksHeading")}
              </Typography>
              <ComoButton
                onClick={() => handleAddLink("knowledgebase")}
                startIcon={<AddCircleOutlineIcon />}
                sx={{
                  px: { xs: 1, sm: 2 },
                  py: { xs: 0.3, sm: 0.8 },
                  fontSize: { xs: "0.7rem", sm: "0.875rem" },
                  minWidth: { xs: "auto", sm: 160 },
                }}
              >
                {t("expertForm.addKnowledgebaseLink")}
              </ComoButton>
            </Box>
            {formState.knowledgebase.map((link: string, index: number) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "10px",
                  px: 3,
                  width: "100%",
                }}
              >
                <ComoInput
                  placeholder={`${t("expertForm.knowledgebaseLinkLabel")} ${index + 1}`}
                  value={link}
                  multiline={false}
                  onChange={(e) => handleLinkChange(e, "knowledgebase", index)}
                />
                <IconButton
                  onClick={() => handleRemoveLink("knowledgebase", index)}
                  sx={{ marginLeft: "10px" }}
                >
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </Box>
            ))}
          </Grid>

          {/* Trends Links */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              paddingLeft: "0 !important",
              marginBottom: "10px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                gap: 2,
                marginBottom: "10px",
                pl: 3,
                pr: 3,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  color: workspace.primary_dark,
                  fontWeight: 600,
                  textAlign: "left",
                }}
              >
                {t("expertForm.trendsLinksHeading")}
              </Typography>
              <ComoButton
                onClick={() => handleAddLink("trends")}
                startIcon={<AddCircleOutlineIcon />}
                sx={{
                  px: { xs: 1, sm: 2 },
                  py: { xs: 0.3, sm: 0.8 },
                  fontSize: { xs: "0.7rem", sm: "0.875rem" },
                  minWidth: { xs: "auto", sm: 160 },
                }}
              >
                {t("expertForm.addTrendsLink")}
              </ComoButton>
            </Box>
            {formState.trends.map((link: string, index: number) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "10px",
                  width: "100%",
                }}
              >
                <ComoInput
                  placeholder={`${t("expertForm.trendsLinkLabel")} ${index + 1}`}
                  value={link}
                  multiline={false}
                  onChange={(e) => handleLinkChange(e, "trends", index)}
                />
                <IconButton
                  onClick={() => handleRemoveLink("trends", index)}
                  sx={{ marginLeft: "10px" }}
                >
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </Box>
            ))}
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
};

export default KnowledgeBaseAndCurrentTrends;
